import axios from 'axios';
import { BASE_URL } from '../configs/api';
import { logoutAndRedirect } from './logoutService';

// Create axios instance with base configuration
const axiosServices = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Helper function to get access token
const getAccessToken = () => {
  try {
    const token = localStorage.getItem("accessToken");
    return token ? JSON.parse(token) : null;
  } catch (error) {
    console.error('Error parsing access token:', error);
    return null;
  }
};

// Helper function to handle logout
const handleLogout = () => {
  console.log('Handling unauthorized access - logging out');
  try {
    // Revoke tokens server-side before redirecting
    logoutAndRedirect('/');
  } catch (error) {
    console.error('Error during logout:', error);
    // Fallback redirect
    window.location.href = '/';
  }
};

// Request interceptor
axiosServices.interceptors.request.use(
  async (config) => {
    const accessToken = getAccessToken();
    
    if (accessToken?.accessToken) {
      config.headers.Authorization = `Bearer ${accessToken.accessToken}`;
    }
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
axiosServices.interceptors.response.use(
  (response) => {
    return response.data;
  },
  async (error) => {
    console.log('Response error:', {
      status: error?.response?.status,
      url: error?.config?.url,
      message: error?.response?.data?.message || error.message
    });

    if (!error.response) {
      console.error('Network Error:', error);
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized - attempt a token refresh before forcing logout
    if (error.response.status === 401) {
      const originalRequest = error.config;
      const tokenData = getAccessToken();

      if (tokenData?.refreshToken && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const refreshResponse = await axios.post(`${BASE_URL}Auth/refresh`, {
            refreshToken: tokenData.refreshToken,
          });
          const newTokens = refreshResponse?.data?.data;

          if (newTokens?.accessToken) {
            localStorage.setItem("accessToken", JSON.stringify(newTokens, null, 2));
            originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
            return axiosServices(originalRequest);
          }
        } catch (refreshError) {
          console.log('Token refresh failed - initiating logout');
        }
      }

      console.log('401 Unauthorized detected - initiating logout');
      // Add a small delay to ensure logs are sent
      setTimeout(() => {
        handleLogout();
      }, 100);
    }

    return Promise.reject(error);
  }
);

export default axiosServices;